// The same invoice as a printable HTML page, for "Save as PDF" from the
// browser's print dialog. Mirrors the spreadsheet: column proportions from
// COLUMN_WIDTHS, the same rows, colours and text. The page title is the file
// name, which browsers offer as the PDF's name.
import { INVOICE_COLOURS as C, INVOICE_FONT_STACK, INVOICE_BODY_STACK, COLUMN_WIDTHS, FROM_DEFAULT, PAYMENT_DEFAULT, PAYMENT_TERMS, FOOTER, LOGO_SIZE, LINE_KINDS, kindOf } from "./house";
import { amountValue, subtotal, fmtUsd } from "./amount";
import { invoiceFilename } from "./prefill";
import { OBVIOUSLY_BOLD_B64 } from "./obviouslyBold";

const esc = s => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const fmtIssueDate = iso => { const [y, m, d] = iso.split("-").map(Number); return `${d} ${MONTHS[m - 1]} ${y}`; };

export function invoiceHtml(cfg, logoSrc) {
  const frm = { ...FROM_DEFAULT, ...(cfg.from || {}) };
  const bt = cfg.bill_to || {};
  const pay = cfg.payment_details || PAYMENT_DEFAULT;
  const items = cfg.line_items || [];
  const sum = subtotal(items);
  const totalW = Object.values(COLUMN_WIDTHS).reduce((a, b) => a + b, 0);
  const cols = Object.values(COLUMN_WIDTHS).map(w => `<col style="width:${(w / totalW * 100).toFixed(3)}%">`).join("");
  const amountCell = a => {
    const v = amountValue(a);
    if (v !== null) return fmtUsd(v);
    return esc(a);
  };
  const row = (inner, style = "") => `<tr${style ? ` style="${style}"` : ""}><td></td>${inner}<td></td></tr>`;
  const blank = (h = 18) => `<tr style="height:${h}px"><td colspan="9"></td></tr>`;
  // Three groups, each under its own heading; zebra restarts per group and
  // covers B:H only (A and I stay margins). Empty groups are not printed.
  const lineRows = LINE_KINDS.map(([kind, title]) => {
    const group = items.filter(it => kindOf(it) === kind);
    if (group.length === 0) return "";
    const head = `<tr class="group"><td></td><td colspan="7">${esc(title)}</td><td></td></tr>`;
    return head + group.map((it, i) => {
      const bg = ` style="background:#${i % 2 === 0 ? C.altFill : C.white}"`;
      return `<tr><td></td><td colspan="3" class="wrap"${bg}>${esc(it.description)}</td><td${bg}></td><td class="grey"${bg}>${esc(cfg.currency || "USD")}</td><td${bg}></td><td class="r"${bg}>${amountCell(it.amount)}</td><td></td></tr>`;
    }).join("");
  }).join("");
  const payRows = pay.map(([l, v]) => `<div class="pk">${esc(l)}</div><div class="pv">${esc(v)}</div>`).join("");

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(invoiceFilename(cfg, "pdf").replace(/\.pdf$/, ""))}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&display=swap" rel="stylesheet">
<style>
@font-face { font-family: "Obviously"; font-weight: 700; font-style: normal; src: url(data:font/otf;base64,${OBVIOUSLY_BOLD_B64}) format("opentype"); }
@page { size: A4 portrait; margin: 12mm 14mm 10mm; }
* { box-sizing: border-box; }
body { margin: 0; background: #fff; color: #${C.body}; font-family: ${INVOICE_BODY_STACK}; font-size: 8pt; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.sheet { width: 182mm; min-height: 275mm; margin: 0 auto; display: flex; flex-direction: column; }
@media screen { body { padding: 24px 0; background: #eee; } .sheet { background: #fff; padding: 0 0 10mm; box-shadow: 0 1px 4px rgba(0,0,0,.15); } }
table { width: 100%; border-collapse: collapse; table-layout: fixed; }
td { padding: 2px 4px; vertical-align: top; line-height: 1.35; }
tr { break-inside: avoid; }
.band td { background: #${C.band}; height: 14px; padding: 0; }
.grey { color: #${C.greyText}; }
.lbl { color: #${C.darkLabel}; font-family: ${INVOICE_FONT_STACK}; font-weight: 700; }
.pine { color: #${C.pine}; }
.b { font-family: ${INVOICE_FONT_STACK}; font-weight: 700; }
.r { text-align: right; }
.wrap { white-space: pre-wrap; word-wrap: break-word; }
.title { font-size: 18pt; text-align: right; vertical-align: middle; letter-spacing: 0.04em; font-family: ${INVOICE_FONT_STACK}; font-weight: 700; }
.small { font-size: 7.5pt; }
.items td { vertical-align: middle; padding: 5px 4px; }
.items tr.group td { background: transparent; color: #${C.pine}; font-family: ${INVOICE_FONT_STACK}; font-weight: 700; font-size: 6.5pt; letter-spacing: 0.08em; text-transform: uppercase; padding: 14px 4px 3px; }
.totals td { padding: 3px 4px; }
.pay { margin-top: auto; padding-top: 10mm; }
.pay .terms { color: #${C.body}; margin: 0 0 8px; line-height: 1.5; font-size: 7pt; }
.pay .grid { display: grid; grid-template-columns: max-content 1fr; column-gap: 12px; row-gap: 1px; font-size: 7pt; line-height: 1.4; color: #${C.body}; }
.pay .pk { font-weight: 400; }
.thanks { font-size: 8.5pt; color: #${C.body}; margin-top: 14px; font-weight: 400; }
.foot { font-size: 7pt; color: #${C.body}; margin-top: 2px; font-weight: 400; }
.side { padding: 0 4.9%; }
</style></head><body><div class="sheet"><table>${cols}
<tr class="band"><td colspan="9"></td></tr>
${blank(14)}
<tr style="height:60px"><td></td><td colspan="2" style="vertical-align:middle">${logoSrc ? `<img src="${esc(logoSrc)}" alt="Thread" width="${Math.round(LOGO_SIZE.width * 0.8)}" height="${Math.round(LOGO_SIZE.height * 0.8)}">` : ""}</td><td colspan="5" class="title">INVOICE</td><td></td></tr>
${blank(18)}
${row(`<td colspan="4" class="grey small">${esc(frm.entity)}</td><td class="r lbl">Date</td><td colspan="2" class="r" style="white-space:nowrap">${esc(fmtIssueDate(cfg.issue_date))}</td>`)}
${row(`<td colspan="4" class="grey small wrap">${esc(frm.address)}</td><td class="r lbl" style="white-space:nowrap">Invoice number</td><td colspan="2" class="r">${esc(cfg.invoice_number)}</td>`)}
${row(`<td colspan="4" class="grey small">${esc(frm.contact)}<br>${esc(frm.website)}</td><td colspan="3"></td>`)}
${blank(28)}
${row(`<td colspan="7" class="lbl">Invoice for</td>`)}
${row(`<td colspan="7">${esc(bt.name)}</td>`)}
${row(`<td colspan="7" class="wrap grey">${esc(bt.address)}</td>`)}
${row(`<td colspan="7" class="grey">${esc(bt.email)}</td>`)}
${blank(30)}
${row(`<td colspan="3" class="pine b">Description</td><td colspan="3"></td><td class="pine b r">Total</td>`)}
</table><table class="items">${cols}${lineRows}</table><table class="totals">${cols}
${blank(8)}
${row(`<td colspan="4"></td><td class="pine r">Subtotal</td><td></td><td class="r">${fmtUsd(sum)}</td>`)}
${row(`<td colspan="4"></td><td class="pine r">GST</td><td></td><td class="r">${esc(cfg.gst ?? "exempt")}</td>`)}
${blank(6)}
${row(`<td colspan="4"></td><td class="pine b r">Total</td><td></td><td class="pine b r">${fmtUsd(sum)}</td>`)}
</table>
<div class="pay side"><div class="terms">Payment<br>${esc(PAYMENT_TERMS)}</div><div class="grid">${payRows}</div>
<div class="thanks">Thank you!</div><div class="foot">${esc(FOOTER)}</div></div></div></body></html>`;
}
